import styled from "@emotion/styled";
import React, { ChangeEvent, useCallback, useEffect, useState } from "react";
import { ApiService } from "src/common";
import { colors } from "src/theme";
import { InputLabel } from "../typography";

const Box = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px'
})

const Input = styled.input((props: { hasError: boolean }) => ({
  boxSizing: 'border-box' as const,
  outline: 'none',
  display: 'block',
  width: '100%',
  height: '44px',
  padding: '9px 12px',
  fontSize: '18px',
  fontWeight: 400,
  fontFamily: "Red Hat Display",
  fontStyle: 'normal',
  lineHeight: '24px',
  color: colors.text1,
  backgroundColor: 'transparent',
  borderRadius: '8px',
  border: `1px solid ${props.hasError ? colors.error1 : colors.border5}`,
  transition: 'border-color .15s ease-in-out, box-shadow .15s ease-in-out',

  '&[disabled]': {
    border: `1px solid ${colors.border6}`,
    color: colors.text4,
    pointerEvents: 'none' as const,
  },

  '&::-moz-placeholder': {
    color: colors.text4,
    opacity: 1,
  },

  '&:-ms-input-placeholder': {
    color: colors.text4,
  },

  '&::-webkit-input-placeholder': {
    color: colors.text4,
  },

  '&:focus::-moz-placeholder': {
    color: 'transparent',
    opacity: 1,
  },

  '&:focus:-ms-input-placeholder': {
    color: 'transparent',
  },

  '&:focus::-webkit-input-placeholder': {
    color: 'transparent',
  },

  '&:focus': {
    borderColor: colors.border4,
  },

  '&[type=number]::-webkit-inner-spin-button, &[type=number]::-webkit-outer-spin-button': {
    WebkitAppearance: 'none' as const,
    MozAppearance: 'none' as const,
    appearance: 'none' as const,
  },
}));

const ValidationError = styled.div({
  position: 'absolute',
  color: colors.error1,
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '1.14',
  marginBottom: 0,
  paddingTop: '4px',
  transition: 'all 0.4s ease',
});

const StreetList = styled.div({
  width: '100%',
  boxSizing: 'border-box' as const,
  outline: 'none',
  display: 'block',
  padding: '9px 12px',
  fontSize: '18px',
  fontWeight: 400,
  fontStyle: 'normal',
  lineHeight: '24px',
  color: colors.text1,
  borderRadius: '8px',
  border: `1px solid ${colors.border5}`,
  transition: 'border-color .15s ease-in-out, box-shadow .15s ease-in-out',
  backgroundColor: '#fff',
  maxHeight: '150px',
  overflowY: 'auto' as const,
  zIndex: 999,
});

const StreetItem = styled.div({
  cursor: 'pointer',

  '&:hover': {
    backgroundColor: colors.background6,
  }
})

export type InputFieldPropsStreet = {
  id?: string;
  label?: string;
  value?: string | number;
  error?: string;
  disabled?: boolean;
  onChange?: (value: { street?: string, streetKompareId?: string, city?: string, cityKompareId: string, zipCode?: number, streetsFound?: boolean }, isValid: boolean) => void;
  onBlur?: () => void;
  cityKompareId?: string;
};

interface StreetData {
  readonly streetName: string
  readonly streetKompareId: string
  readonly cityKompareId: string
  readonly cityName: string
  readonly postalCode: number
}

export const InputStreet: React.FC<InputFieldPropsStreet> = ({
  id,
  label,
  value,
  error,
  disabled,
  onChange,
  onBlur,
  cityKompareId
}) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [showStreetList, setShowStreetList] = useState(false);
  const [streetSelected, setStreetSelected] = useState(false);
  const [inputTimeout, setInputTimeout] = useState<NodeJS.Timeout | null>(null);
  const [streetData, setStreetData] = useState<StreetData[]>([]);

  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  useEffect(() => {
    if (showStreetList && cityKompareId) {
      const streetsFound = streetSelected || streetData.length > 0;
      onChange?.({ street: inputValue as string, cityKompareId, streetsFound }, streetsFound);
    }
  }, [streetData]);

  const fetchStreets = (query: string) => {
    ApiService.getStreets(query, cityKompareId).subscribe({
      next: (streets: StreetData[]) => {
        setStreetData(streets);
      },
      error: () => {
        console.log('Error while getting streets')
      },
    });
  };

  const fetchStreetsDebounced = useCallback((query: string) => {
    if (inputTimeout) {
      clearTimeout(inputTimeout);
    }

    const timeout = setTimeout(() => {
      fetchStreets(query);
    }, 500);

    setInputTimeout(timeout);
  }, [inputTimeout]);

  const resetStreetSelected = () => {
    setStreetSelected(false);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const newValue = e.target.value;

    setInputValue(newValue);

    fetchStreetsDebounced(newValue);

    setShowStreetList(true);
    resetStreetSelected();
  };

  const handleBlur = () => {
    if (onBlur && (inputValue as string).length > 0) onBlur();
  };

  const handleStreetItemClick = (street: StreetData) => {
    setInputValue(street.streetName);
    setStreetSelected(true);
    setShowStreetList(false);
    if (onChange) {
      onChange({ streetKompareId: street.streetKompareId, street: street.streetName, city: street.cityName, cityKompareId: street.cityKompareId, zipCode: street.postalCode, streetsFound: true }, !!street.streetKompareId);
    }
  };

  return (
    <Box>
      <div>
        {label ? <InputLabel htmlFor={id} disabled={disabled}>{label}</InputLabel> : null}
        <Input
          autoComplete="new-street-lvl2"
          id={id}
          type='text'
          value={inputValue}
          disabled={disabled}
          onChange={handleChange}
          onBlur={handleBlur}
          hasError={!!error}
        />
        {error ? <ValidationError>{error}</ValidationError> : null}
      </div>
      {showStreetList && streetData && streetData.length > 0 && !streetSelected && (
        <StreetList>
          {streetData.length === 0 || streetData.every(street => !street.streetName) ? (
            <StreetItem>Ne postoji ulica</StreetItem>
          ) : (
            streetData.map(street => (
              <StreetItem key={street.streetKompareId} onClick={() => handleStreetItemClick(street)}>
                {cityKompareId ? street.streetName : `${street.streetName}, ${street.cityName} ${street.postalCode}`}
              </StreetItem>
            ))
          )}
        </StreetList>
      )}
    </Box>
  );
};
